import { useState, useEffect, useCallback } from 'react';
import type { FieldErrors } from 'react-hook-form';
import { Box } from '@mui/material';

import { apiRequest } from '@/src/services';
import {
    ControlledChipMultiSelect,
    ControlledSingleSelect,
    ControlledCheckbox,
    Loading,
    ErrorMessage,
    SimpleButton,
} from '@/src/ui';
import { getZeroOption, type Option } from '@/src/utils';
import { type QuestionMetaDTO, MathSymbols } from '@/src/lib';
import type { QuestionMetaOptionsDTO, StandardOption } from '../../models/questionDTOs';

const SelectGradeOption = getZeroOption('Select Grade(s)');

interface QuestionMetaFormProps {
    rhfProps: {
        control: any;
        errors: FieldErrors<QuestionMetaDTO> | undefined;
        watch: any;
    };
    handleLanguageSet?: (lang: 'en' | 'es') => void;
    metaValues: QuestionMetaDTO;
    disableList?: string[]; // Disable certain options for certain question types
    setMetaValues: (meta: QuestionMetaDTO) => void;
}

function QuestionMetaForm({
    rhfProps,
    metaValues,
    setMetaValues,
    disableList,
    handleLanguageSet,
}: QuestionMetaFormProps) {
    const [loaded, setLoaded] = useState(false);
    const [error, setError] = useState('');

    const [difficultyOptions, setDifficultyOptions] = useState<Option[]>([]);
    const [claimOptions, setClaimOptions] = useState<Option[]>([]);
    const [targetOptions, setTargetOptions] = useState<Option[]>([]);
    const [dokOptions, setDokOptions] = useState<Option[]>([]);
    const [gradeOptions, setGradeOptions] = useState<Option[]>([]);
    const [standardOptions, setStandardOptions] = useState<Option[]>([]);
    const [standardData, setStandardData] = useState<StandardOption[]>([]);

    const { control, errors = {} } = rhfProps;

    // Filter standards based on selected grades
    const updateStandardOptions = useCallback(
        (data: StandardOption[], selectedGrades: number[]) => {
            let filteredStandardOptions: Option[];
            if (!selectedGrades || selectedGrades.length === 0) {
                filteredStandardOptions = [SelectGradeOption];
            } else {
                filteredStandardOptions = data.filter((opt) =>
                    selectedGrades.includes(opt.gradeLevelMasterId)
                );
            }
            setStandardOptions(filteredStandardOptions);
        },
        []
    );

    const updateKeypadSettings = useCallback(
        (mode: 'clear' | 'symbols' | 'letters_upper' | 'letters_lower' | 'numbers') => {
            let newKeypadSetting = [...metaValues.keypadSetting];
            switch (mode) {
                case 'clear':
                    newKeypadSetting = [];
                    break;
                case 'letters_lower':
                    Object.keys(MathSymbols.letters_lower).forEach((key) => {
                        if (!newKeypadSetting.includes(key)) newKeypadSetting.push(key);
                    });
                    break;
                case 'letters_upper':
                    Object.keys(MathSymbols.letters_upper).forEach((key) => {
                        if (!newKeypadSetting.includes(key)) newKeypadSetting.push(key);
                    });
                    break;
                case 'symbols':
                    Object.keys(MathSymbols.symbols).forEach((key) => {
                        if (!newKeypadSetting.includes(key)) newKeypadSetting.push(key);
                    });
                    break;
                case 'numbers':
                    Object.keys(MathSymbols.numbers).forEach((key) => {
                        if (!newKeypadSetting.includes(key)) newKeypadSetting.push(key);
                    });
                    break;
                default:
                    break;
            }

            setMetaValues({ ...metaValues, keypadSetting: newKeypadSetting });
        },
        [metaValues, setMetaValues]
    );

    const renderKeypadCheckboxes = useCallback(
        (values: Record<string, string>) => {
            return (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                    {Object.entries(values).map(([key, value]) => (
                        <ControlledCheckbox
                            key={`keypad-checkbox-${key}`}
                            name={`meta.keypadSetting`}
                            label={value}
                            control={control}
                            checked={
                                Array.isArray(metaValues.keypadSetting)
                                    ? metaValues.keypadSetting.includes(key)
                                    : false
                            }
                            onChange={(e, field) => {
                                let newArr = Array.isArray(metaValues.keypadSetting)
                                    ? [...metaValues.keypadSetting]
                                    : [];
                                if (e.target.checked) {
                                    if (!newArr.includes(key)) newArr.push(key);
                                } else {
                                    newArr = newArr.filter((n) => n !== key);
                                }
                                setMetaValues({
                                    ...metaValues,
                                    keypadSetting: newArr.sort(),
                                });
                                field.onChange(newArr);
                            }}
                        />
                    ))}
                </Box>
            );
        },
        [metaValues, setMetaValues, control]
    );

    useEffect(() => {
        (async function init() {
            try {
                const res = await apiRequest<QuestionMetaOptionsDTO>(
                    'GET',
                    'utility/filterData?getDifficultyLevelMaster=true' +
                        '&getPrimaryClaimMaster=true' +
                        '&getPrimaryTargetMaster=true' +
                        '&getDepthOfKnowledgeMaster=true' +
                        '&getGradeLevelMaster=true' +
                        '&getStandard=true'
                );
                const {
                    difficultyLevelMasterOptions,
                    primaryClaimMasterOptions,
                    primaryTargetMasterOptions,
                    depthOfKnowledgeMasterOptions,
                    gradeLevelMasterOptions,
                    standardOptions,
                } = res.data;

                setDifficultyOptions(difficultyLevelMasterOptions);
                setClaimOptions(primaryClaimMasterOptions);
                setTargetOptions(primaryTargetMasterOptions);
                setDokOptions(depthOfKnowledgeMasterOptions);
                setGradeOptions([SelectGradeOption, ...gradeLevelMasterOptions]);
                if (metaValues.gradeLevelMasterIds.length > 0) {
                    updateStandardOptions(standardOptions, metaValues.gradeLevelMasterIds);
                } else {
                    setStandardOptions(standardOptions.map((s) => ({ id: s.id, name: s.name })));
                }
                setStandardData(standardOptions);
            } catch {
                setError('Failed to fetch meta data');
            } finally {
                setLoaded(true);
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <>
            {error && <ErrorMessage error={error} />}
            {loaded ? (
                <>
                    <div style={{ display: 'flex', gap: 16 }}>
                        <div style={{ flex: 1, maxWidth: 220 }}>
                            <ControlledChipMultiSelect
                                name="meta.gradeLevelMasterIds"
                                label="Grade"
                                options={gradeOptions}
                                control={control}
                                error={errors.gradeLevelMasterIds}
                                onChange={(event, field) => {
                                    const newGradeOptions = Array.isArray(event.target.value)
                                        ? event.target.value
                                        : [event.target.value];
                                    if (!newGradeOptions.some((g) => Number(g) === 0)) {
                                        field.onChange(newGradeOptions);
                                        const newStandards = metaValues.standards.filter(
                                            (standardId) => {
                                                const stdOption = standardData.find(
                                                    (opt) => opt.id === standardId
                                                );
                                                return (
                                                    stdOption &&
                                                    newGradeOptions.includes(
                                                        stdOption.gradeLevelMasterId
                                                    )
                                                );
                                            }
                                        );

                                        updateStandardOptions(standardData, newGradeOptions);
                                        setMetaValues({
                                            ...metaValues,
                                            gradeLevelMasterIds: newGradeOptions,
                                            standards: newStandards,
                                        });
                                    }
                                }}
                            />
                        </div>
                        <div style={{ flex: 2, maxWidth: 350 }}>
                            <ControlledChipMultiSelect
                                name="meta.standards"
                                label="Standard"
                                options={standardOptions}
                                control={control}
                                error={errors.standards}
                                onChange={(event, field) => {
                                    const value = Array.isArray(event.target.value)
                                        ? event.target.value
                                        : [event.target.value];
                                    if (!value.some((v) => Number(v) === 0)) {
                                        field.onChange(value);
                                        setMetaValues({ ...metaValues, standards: value });
                                    }
                                }}
                            />
                        </div>
                    </div>

                    <div style={{ display: 'flex', gap: 16, marginTop: 16 }}>
                        <div style={{ flex: 1 }}>
                            <ControlledSingleSelect
                                name="meta.difficultyLevelMasterId"
                                label="Difficulty"
                                control={control}
                                options={difficultyOptions}
                                error={errors.difficultyLevelMasterId}
                                onChange={(e, field) => {
                                    const value = e.target.value;
                                    setMetaValues({
                                        ...metaValues,
                                        difficultyLevelMasterId: Number(value),
                                    });
                                    field.onChange(value);
                                }}
                            />
                        </div>
                        <div style={{ flex: 1 }}>
                            <ControlledSingleSelect
                                name="meta.primaryClaimMasterId"
                                label="Primary Claim"
                                control={control}
                                options={claimOptions}
                                error={errors.primaryClaimMasterId}
                                onChange={(e, field) => {
                                    const value = e.target.value;
                                    setMetaValues({
                                        ...metaValues,
                                        primaryClaimMasterId: Number(value),
                                    });
                                    field.onChange(value);
                                }}
                            />
                        </div>
                        <div style={{ flex: 1 }}>
                            <ControlledSingleSelect
                                name="meta.primaryTargetMasterId"
                                label="Primary Target"
                                control={control}
                                options={targetOptions}
                                error={errors.primaryTargetMasterId}
                                onChange={(e, field) => {
                                    const value = e.target.value;
                                    setMetaValues({
                                        ...metaValues,
                                        primaryTargetMasterId: Number(value),
                                    });
                                    field.onChange(value);
                                }}
                            />
                        </div>
                        <div style={{ flex: 1 }}>
                            <ControlledSingleSelect
                                name="meta.depthOfKnowledgeMasterId"
                                label="Depth of Knowledge"
                                control={control}
                                options={dokOptions}
                                error={errors.depthOfKnowledgeMasterId}
                                onChange={(e, field) => {
                                    const value = e.target.value;
                                    setMetaValues({
                                        ...metaValues,
                                        depthOfKnowledgeMasterId: Number(value),
                                    });
                                    field.onChange(value);
                                }}
                            />
                        </div>
                    </div>

                    <div style={{ display: 'flex', gap: 16, marginTop: 16 }}>
                        <div style={{ flex: 1 }}>
                            <ControlledCheckbox
                                name="meta.isPracticeQuestion"
                                label="Is Practice Question"
                                disabled={disableList?.includes('isPracticeQuestion')}
                                onChange={(e, field) => {
                                    const checked = e.target.checked;
                                    setMetaValues({ ...metaValues, isPracticeQuestion: checked });
                                    field.onChange(checked);
                                }}
                                control={control}
                            />
                            {errors.isPracticeQuestion && (
                                <ErrorMessage error={errors.isPracticeQuestion.message} />
                            )}
                        </div>
                        <div style={{ flex: 1 }}>
                            <ControlledCheckbox
                                name="meta.isHandScored"
                                label="Hand Scored"
                                disabled={disableList?.includes('isHandScored')}
                                onChange={(e, field) => {
                                    const checked = e.target.checked;
                                    setMetaValues({ ...metaValues, isHandScored: checked });
                                    field.onChange(checked);
                                }}
                                control={control}
                            />
                            {errors.isHandScored && (
                                <ErrorMessage error={errors.isHandScored.message} />
                            )}
                        </div>
                        {/* <div style={{ flex: 1 }}>
                            <ControlledCheckbox
                                name="meta.shouldShuffleOptions"
                                label="Shuffle Options"
                                disabled={disableList?.includes('shouldShuffleOptions')}
                                onChange={(e, field) => {
                                    const checked = e.target.checked;
                                    setMetaValues({ ...metaValues, shouldShuffleOptions: checked });
                                    field.onChange(checked);
                                }}
                                control={control}
                            />
                            {errors.shouldShuffleOptions && (
                                <ErrorMessage error={errors.shouldShuffleOptions.message} />
                            )}
                        </div> */}
                        <div style={{ flex: 1 }}>
                            <ControlledCheckbox
                                name="meta.shouldShowKeypad"
                                label="Show Keypad"
                                disabled={disableList?.includes('shouldShowKeypad')}
                                onChange={(e, field) => {
                                    const checked = e.target.checked;
                                    setMetaValues({ ...metaValues, shouldShowKeypad: checked });
                                    field.onChange(checked);
                                }}
                                control={control}
                            />
                            {errors.shouldShowKeypad && (
                                <ErrorMessage error={errors.shouldShowKeypad.message} />
                            )}
                        </div>
                        <div style={{ flex: 1 }}>
                            <ControlledCheckbox
                                name="meta.isSpanishAllowed"
                                label="Is Spanish Allowed"
                                disabled={disableList?.includes('isSpanishAllowed')}
                                onChange={(e, field) => {
                                    const checked = e.target.checked;
                                    if (handleLanguageSet) {
                                        handleLanguageSet(checked ? 'es' : 'en');
                                    }
                                    setMetaValues({ ...metaValues, isSpanishAllowed: checked });
                                    field.onChange(checked);
                                }}
                                control={control}
                            />
                            {errors.isSpanishAllowed && (
                                <ErrorMessage error={errors.isSpanishAllowed.message} />
                            )}
                        </div>
                    </div>

                    {/* Keypad Setting Checkboxes */}
                    {metaValues.shouldShowKeypad && (
                        <div style={{ alignItems: 'center' }}>
                            <div style={{ display: 'flex', marginBottom: '20px', gap: '10px' }}>
                                <span>Keypad Setting:</span>
                                <SimpleButton onClick={() => updateKeypadSettings('numbers')}>
                                    Add Numbers
                                </SimpleButton>
                                <SimpleButton onClick={() => updateKeypadSettings('letters_lower')}>
                                    Add Lowercase
                                </SimpleButton>
                                <SimpleButton onClick={() => updateKeypadSettings('letters_upper')}>
                                    Add Uppercase
                                </SimpleButton>
                                <SimpleButton onClick={() => updateKeypadSettings('symbols')}>
                                    Add Symbols
                                </SimpleButton>
                                <SimpleButton onClick={() => updateKeypadSettings('clear')}>
                                    Clear
                                </SimpleButton>
                            </div>

                            <div>Numbers:</div>
                            {renderKeypadCheckboxes(MathSymbols.numbers)}

                            <div>Letters (lower):</div>
                            {renderKeypadCheckboxes(MathSymbols.letters_lower)}

                            <div>Letters (upper):</div>
                            {renderKeypadCheckboxes(MathSymbols.letters_upper)}

                            <div>Symbols:</div>
                            {renderKeypadCheckboxes(MathSymbols.symbols)}

                            <div>Expressions:</div>
                            {renderKeypadCheckboxes(MathSymbols.expressions)}
                            {renderKeypadCheckboxes({ free: 'Free' }) /* freeform input */}
                        </div>
                    )}
                </>
            ) : (
                <Loading message="Loading..." size="md" />
            )}
        </>
    );
}

export default QuestionMetaForm;
